import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import {
  Plus,
  X,
  Loader2,
  RotateCcw,
  Lock,
  Users,
  UserCheck,
  Link2,
  Download,
  Search,
  LayoutGrid,
  List,
  Star,
  Trash2,
  Clock3,
  ChevronDown,
  ChevronRight,
  Code,
  LayoutTemplate,
} from 'lucide-react'
import {
  listMyApps,
  listDeletedApps,
  listSharedApps,
  listFavoriteApps,
  listTemplates,
  listCategories,
  deleteApp,
  permanentlyDeleteApp,
  restoreApp,
  startApp,
  stopApp,
  rebuildApp,
  favoriteApp,
  unfavoriteApp,
  type App,
  type DeletedApp,
  type Folder,
} from '../lib/api'
import CreateAppModal from '../components/CreateAppModal'
import SplitButton from '../components/SplitButton'
import TemplateGalleryModal from '../components/TemplateGalleryModal'
import EditAppInfoModal from '../components/EditAppInfoModal'
import ShareModalComponent from '../components/ShareModal'
import CloneAppModal from '../components/CloneAppModal'
import ImportProjectModal from '../components/ImportProjectModal'
import { buildCategoryConfig, type CategoryStyle } from '../lib/categoryColors'
import { buildTemplateSplitButtonProps } from '../lib/templateGating'
import { getCloneNotice } from '../lib/cloneFeedback'
import { withMountBase } from '../lib/mountBase'
import { formatRelativeTime } from '../lib/format'
import AppActions from '../components/AppActions'
import { VisitorBadge } from '../components/VisitorBadge'
import { HelpLink } from '../components/HelpLink'
import {
  BrowseGridCard,
  BrowseListCard,
  FeaturedIcon,
  PopularIcon,
  FavoriteButton,
  PermissionBadge,
  StatusBadge,
  AvatarWithTooltip,
} from '../components/BrowseCard'
import { FolderSection } from '../components/folders/FolderSection'
import { useFolders } from '../components/folders/useFolders'
import { AppContextMenu } from '../components/folders/AppContextMenu'
import { StyledSelect } from '../components/ui/StyledSelect'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'


const accessLevelConfig = {
  personal: { icon: Lock, label: 'Private' },
  specific: { icon: UserCheck, label: 'Shared' },
  link: { icon: Link2, label: 'Link' },
  platform: { icon: Users, label: 'Platform' },
}

const statusConfig: Record<string, { color: string; label: string; pulse: boolean }> = {
  running: { color: 'bg-green-500', label: 'Running', pulse: true },
  stopped: { color: 'bg-gray-500', label: 'Stopped', pulse: false },
  starting: { color: 'bg-yellow-500', label: 'Starting', pulse: true },
  error: { color: 'bg-red-500', label: 'Error', pulse: false },
}

let categoryConfig: Record<string, CategoryStyle> = {}


// Helper: classify an app's source
function getAppSource(app: App): 'mine' | 'shared' | 'browse' {
  if (app.is_owner || app.my_permission === 'owner') return 'mine'
  if (app.my_permission === 'platform') return 'browse'
  if (app.my_permission) return 'shared'
  return 'browse' // fallback for apps with no permission info
}

export default function MyApps() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const user = useUser()
  const [apps, setApps] = useState<App[]>([])
  const [deletedApps, setDeletedApps] = useState<DeletedApp[]>([])
  const [sharedApps, setSharedApps] = useState<App[]>([])
  const [favoriteApps, setFavoriteApps] = useState<App[]>([])
  const [templateApps, setTemplateApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [showTemplateGallery, setShowTemplateGallery] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editApp, setEditApp] = useState<App | null>(null)
  const [showShare, setShowShare] = useState<App | null>(null)
  const [cloneTarget, setCloneTarget] = useState<App | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [workspaceLaunchMessage, setWorkspaceLaunchMessage] = useState<string | null>(null)

  // Source filter + favorites toggle (replaces tabs)
  const [sourceFilter, setSourceFilter] = useState<'my-apps' | 'shared' | 'all' | 'templates'>(() => {
    const saved = localStorage.getItem('myapps-source-filter') as 'my-apps' | 'shared' | 'all' | 'templates'
    // Don't restore 'templates' tab for non-admins
    if (saved === 'templates') return 'my-apps'
    return saved || 'my-apps'
  })
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => localStorage.getItem('myapps-favorites-only') === 'true')

  // Search, filter, sort, view state
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('myapps-status-filter') || 'all'
  })
  const [accessFilter, setAccessFilter] = useState(() => {
    return localStorage.getItem('myapps-access-filter') || 'all'
  })
  const [permissionFilter, setPermissionFilter] = useState('all')
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('myapps-sort') || 'recent'
  })
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (localStorage.getItem('myapps-view') as 'grid' | 'list') || 'grid'
  })
  const [restoringAppNames, setRestoringAppNames] = useState<string[]>([])
  const [permanentlyDeletingAppNames, setPermanentlyDeletingAppNames] = useState<string[]>([])
  const [showDeletedApps, setShowDeletedApps] = useState(false)

  // Merged, deduplicated app list
  const mergedApps = useMemo(() => {
    const byName = new Map<string, App>()
    for (const app of apps) byName.set(app.name, app)
    for (const app of sharedApps) if (!byName.has(app.name)) byName.set(app.name, app)
    for (const app of favoriteApps) if (!byName.has(app.name)) byName.set(app.name, app)
    return Array.from(byName.values())
  }, [apps, sharedApps, favoriteApps])

  // Folder organization — uses merged list so folders work cross-source
  const {
    folders,
    activeFolderId,
    activeFolderApps,
    collapsed: foldersCollapsed,
    setCollapsed: setFoldersCollapsed,
    navigateToFolder,
    navigateToRoot,
    createFolder,
    renameFolder,
    changeFolderColor,
    deleteFolder: deleteFolderAction,
    addAppToFolder,
    removeAppFromFolder,
    getAppFolderIds,
    createFolderAndAddApp,
    getFolderAppNames,
  } = useFolders(mergedApps, 'my-apps')

  useEffect(() => {
    loadApps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    localStorage.setItem('myapps-view', viewMode)
  }, [viewMode])

  useEffect(() => {
    localStorage.setItem('myapps-source-filter', sourceFilter)
  }, [sourceFilter])

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'shared' || tab === 'all' || tab === 'my-apps') {
      setSourceFilter(tab)
    } else if (tab === 'templates' && user.isAdmin) {
      setSourceFilter(tab)
    }
  }, [searchParams, user.isAdmin])

  useEffect(() => {
    localStorage.setItem('myapps-sort', sortBy)
  }, [sortBy])

  useEffect(() => {
    localStorage.setItem('myapps-status-filter', statusFilter)
  }, [statusFilter])

  useEffect(() => {
    localStorage.setItem('myapps-access-filter', accessFilter)
  }, [accessFilter])

  useEffect(() => {
    localStorage.setItem('myapps-favorites-only', String(showFavoritesOnly))
  }, [showFavoritesOnly])

  useEffect(() => {
    if (deletedApps.length === 0) {
      setShowDeletedApps(false)
    }
  }, [deletedApps.length])

  const loadApps = useCallback(async function loadApps() {
    try {
      const [myApps, deleted, shared, favorites] = await Promise.all([
        listMyApps(),
        listDeletedApps(),
        listSharedApps(),
        listFavoriteApps(),
      ])
      setApps(myApps)
      setDeletedApps(deleted)
      setSharedApps(shared)
      setFavoriteApps(favorites)
      // Fetch templates for admin users
      if (user.isAdmin) {
        try {
          const templates = await listTemplates()
          setTemplateApps(templates)
        } catch {
          setTemplateApps([])
        }
      }
      try {
        const cats = await listCategories()
        categoryConfig = buildCategoryConfig(cats)
      } catch {
        categoryConfig = {}
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps')
    } finally {
      setLoading(false)
    }
  }, [user.isAdmin])

  // Unified filtered + sorted app list
  const filteredApps = useMemo(() => {
    // Templates tab uses its own data source
    if (sourceFilter === 'templates') {
      return templateApps
        .filter(app => {
          const matchesSearch = search === '' ||
            (app.display_name || app.name).toLowerCase().includes(search.toLowerCase()) ||
            (app.description || '').toLowerCase().includes(search.toLowerCase())
          const matchesStatus = statusFilter === 'all' || app.status === statusFilter
          return matchesSearch && matchesStatus
        })
        .sort((a, b) => {
          switch (sortBy) {
            case 'name-asc':
              return (a.display_name || a.name).localeCompare(b.display_name || b.name)
            case 'name-desc':
              return (b.display_name || b.name).localeCompare(a.display_name || a.name)
            default:
              return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
          }
        })
    }

    // Start with folder contents or full merged list
    let base = activeFolderId ? activeFolderApps : mergedApps

    // Filter by source
    if (sourceFilter === 'my-apps') {
      base = base.filter(app => getAppSource(app) === 'mine')
    } else if (sourceFilter === 'shared') {
      base = base.filter(app => getAppSource(app) === 'shared')
    }
    // 'all' shows everything

    // Filter by favorites toggle
    if (showFavoritesOnly) {
      base = base.filter(app => app.is_favorited)
    }

    return base
      .filter(app => {
        const matchesSearch = search === '' ||
          (app.display_name || app.name).toLowerCase().includes(search.toLowerCase()) ||
          (app.description || '').toLowerCase().includes(search.toLowerCase()) ||
          (app.owner_name || app.owner_email || '').toLowerCase().includes(search.toLowerCase())

        const matchesStatus = statusFilter === 'all' || app.status === statusFilter

        // Access filter — applies to owned apps (non-owned apps pass through)
        const source = getAppSource(app)
        const matchesAccess = accessFilter === 'all' || source !== 'mine' || app.access_level === accessFilter

        // Permission filter — applies to non-owned apps (owned apps pass through)
        const matchesPerm = permissionFilter === 'all' || source === 'mine' ||
          (permissionFilter === 'edit' ? (app.my_permission === 'edit' || app.my_permission === 'admin') : app.my_permission === permissionFilter)

        return matchesSearch && matchesStatus && matchesAccess && matchesPerm
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return (a.display_name || a.name).localeCompare(b.display_name || b.name)
          case 'name-desc':
            return (b.display_name || b.name).localeCompare(a.display_name || a.name)
          case 'visitors':
            return (b.visit_count_30d || 0) - (a.visit_count_30d || 0)
          default: // 'recent'
            return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
        }
      })
  }, [mergedApps, activeFolderId, activeFolderApps, sourceFilter, showFavoritesOnly, search, statusFilter, accessFilter, permissionFilter, sortBy, templateApps])

  const filteredDeletedApps = useMemo(() => {
    return deletedApps.filter(app => {
      return search === '' ||
        (app.display_name || app.name).toLowerCase().includes(search.toLowerCase()) ||
        (app.description || '').toLowerCase().includes(search.toLowerCase())
    })
  }, [deletedApps, search])

  const hasActiveFilters = search !== '' || statusFilter !== 'all' || accessFilter !== 'all' || permissionFilter !== 'all' || showFavoritesOnly
  const deletedSearchMatches = filteredDeletedApps.length

  function clearFilters() {
    setSearch('')
    setStatusFilter('all')
    setAccessFilter('all')
    setPermissionFilter('all')
    setShowFavoritesOnly(false)
  }

  async function handleDelete(app: App) {
    setError(null)
    try {
      const deleted = await deleteApp(app.name)
      setApps(prev => prev.filter(candidate => candidate.name !== deleted.name))
      setFavoriteApps(prev => prev.filter(candidate => candidate.name !== deleted.name))
      setDeletedApps(prev => [deleted, ...prev.filter(candidate => candidate.name !== deleted.name)])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete app')
      throw err
    }
  }

  async function handleRestore(app: DeletedApp) {
    const previousDeletedApps = deletedApps
    setError(null)
    setRestoringAppNames(prev => [...prev, app.name])
    setDeletedApps(prev => prev.filter(candidate => candidate.name !== app.name))
    try {
      const restored = await restoreApp(app.name)
      const hydratedRestored = {
        ...restored,
        is_owner: true,
        my_permission: 'owner',
      }
      setApps(prev => [hydratedRestored, ...prev.filter(candidate => candidate.name !== hydratedRestored.name)])
      setSharedApps(prev => prev.filter(candidate => candidate.name !== hydratedRestored.name))
    } catch (err) {
      setDeletedApps(previousDeletedApps)
      setError(err instanceof Error ? err.message : 'Failed to restore app')
    } finally {
      setRestoringAppNames(prev => prev.filter(name => name !== app.name))
    }
  }

  async function handlePermanentDelete(app: DeletedApp) {
    const previousDeletedApps = deletedApps
    setError(null)
    setPermanentlyDeletingAppNames(prev => [...prev, app.name])
    setDeletedApps(prev => prev.filter(candidate => candidate.name !== app.name))
    try {
      await permanentlyDeleteApp(app.name)
    } catch (err) {
      setDeletedApps(previousDeletedApps)
      setError(err instanceof Error ? err.message : 'Failed to permanently delete app')
    } finally {
      setPermanentlyDeletingAppNames(prev => prev.filter(name => name !== app.name))
    }
  }

  async function handleStart(app: App) {
    try {
      await startApp(app.name)
      await loadApps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start app')
    }
  }

  async function handleStop(app: App) {
    try {
      await stopApp(app.name)
      await loadApps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop app')
    }
  }

  async function handleRebuild(app: App) {
    try {
      await rebuildApp(app.name)
      await loadApps()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rebuild app')
    }
  }

  // Unified favorite toggle — updates all three source lists
  async function handleToggleFavorite(app: App) {
    const wasFavorited = app.is_favorited
    // Optimistic update across all lists
    const updater = (a: App) =>
      a.name === app.name ? { ...a, is_favorited: !wasFavorited } : a
    setApps(prev => prev.map(updater))
    setSharedApps(prev => prev.map(updater))
    if (wasFavorited) {
      setFavoriteApps(prev => prev.filter(a => a.name !== app.name))
    } else {
      setFavoriteApps(prev => [...prev, { ...app, is_favorited: true }])
    }
    try {
      if (wasFavorited) {
        await unfavoriteApp(app.name)
      } else {
        await favoriteApp(app.name)
      }
    } catch {
      // Revert on error
      const revert = (a: App) =>
        a.name === app.name ? { ...a, is_favorited: wasFavorited } : a
      setApps(prev => prev.map(revert))
      setSharedApps(prev => prev.map(revert))
      if (wasFavorited) {
        setFavoriteApps(prev => [...prev, { ...app, is_favorited: wasFavorited }])
      } else {
        setFavoriteApps(prev => prev.filter(a => a.name !== app.name))
      }
    }
  }

  function launchWorkspaceWithFeedback(targetUrl: string, message: string) {
    setWorkspaceLaunchMessage(message)
    window.setTimeout(() => {
      window.location.href = targetUrl
    }, 50)
  }

  // Helper: compute permissions and action handlers for any app
  function getAppProps(app: App) {
    const source = getAppSource(app)
    const isOwned = source === 'mine'
    const rawPerm = app.my_permission as 'owner' | 'view' | 'edit' | 'admin' | 'platform' | undefined
    const permission: 'owner' | 'admin' | 'edit' | 'view' =
      rawPerm === 'platform' ? 'view'
      : rawPerm === 'owner' || isOwned ? 'owner'
      : (rawPerm as 'admin' | 'edit' | 'view') || 'view'
    const canEdit = permission === 'owner' || permission === 'admin' || permission === 'edit'

    return {
      isOwned,
      permission,
      canEdit,
      onStart: canEdit ? () => handleStart(app) : undefined,
      onStop: canEdit ? () => handleStop(app) : undefined,
      onRebuild: canEdit ? () => handleRebuild(app) : undefined,
      onRename: canEdit ? () => setEditApp(app) : undefined,
      onPublish: canEdit ? () => navigate(`/apps/${app.name}`) : undefined,
      onShare: canEdit ? () => setShowShare(app) : undefined,
      onClone: canEdit ? () => setCloneTarget(app) : undefined,
      onDelete: isOwned ? () => handleDelete(app) : undefined,
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const hasAnyApps = apps.length > 0 || sharedApps.length > 0 || favoriteApps.length > 0 || templateApps.length > 0
  const showDeletedTray = (sourceFilter === 'my-apps' || sourceFilter === 'all') && deletedApps.length > 0
  const isTemplateTab = sourceFilter === 'templates'

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">My Apps</h1>
          <p className="text-muted-foreground hidden sm:block mt-1">
            Create and manage your deployed applications.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-card border border-border text-foreground rounded-lg hover:bg-secondary transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Import</span>
          </button>
          <SplitButton
            label="Create App"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreate(true)}
            {...buildTemplateSplitButtonProps(user, () => setShowTemplateGallery(true))}
          />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>{error}</span>
            <HelpLink section="apps">View help</HelpLink>
          </div>
          <button onClick={() => setError(null)} className="p-1 hover:bg-destructive/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Create App Modal */}
      {showCreate && (
        <CreateAppModal
          onClose={() => setShowCreate(false)}
          onCreated={(app) => {
            setShowCreate(false)
            const workspacePath = withMountBase('/workspace', window.location.pathname)
            const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
            const appUrl = `${window.location.origin}${appPath}`
            const target = `${workspacePath}?app=${encodeURIComponent(app.name)}&url=${encodeURIComponent(appUrl)}`
            launchWorkspaceWithFeedback(
              target,
              `Preparing workspace for ${app.name}...`,
            )
          }}
        />
      )}

      {/* Template Gallery Modal */}
      {showTemplateGallery && (
        <TemplateGalleryModal onClose={() => setShowTemplateGallery(false)} />
      )}

      {/* Import Project Modal */}
      {showImport && (
        <ImportProjectModal
          onClose={() => setShowImport(false)}
          onImported={(appName, importId) => {
            setShowImport(false)
            const workspacePath = withMountBase('/workspace', window.location.pathname)
            const appPath = withMountBase(`/a/${appName}/`, window.location.pathname)
            const appUrl = `${window.location.origin}${appPath}`
            const params = new URLSearchParams({
              app: appName,
              url: appUrl,
              import_id: String(importId),
            })
            launchWorkspaceWithFeedback(
              `${workspacePath}?${params.toString()}`,
              `Starting import for ${appName}...`,
            )
          }}
        />
      )}

      {/* Edit App Info Modal */}
      {editApp && (
        <EditAppInfoModal
          app={editApp}
          onClose={() => setEditApp(null)}
          onSaved={(updated) => {
            setEditApp(null)
            // Update the app in all lists
            const update = (list: App[]) => list.map(a => a.name === editApp.name ? { ...a, ...updated } : a)
            setApps(update)
            setSharedApps(update)
            setFavoriteApps(update)
          }}
        />
      )}

      {/* Share Modal */}
      {showShare && (
        <ShareModalComponent
          app={showShare}
          onClose={() => setShowShare(null)}
          onShared={loadApps}
        />
      )}

      {/* Clone Modal */}
      {cloneTarget && (
        <CloneAppModal
          app={cloneTarget}
          onClose={() => setCloneTarget(null)}
          onCloned={(response) => {
            setCloneTarget(null)
            navigate(`/apps/${response.name}`, {
              state: { cloneNotice: getCloneNotice(response) },
            })
          }}
        />
      )}

      {/* Toolbar: single row, wraps on mobile */}
      {hasAnyApps && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search apps..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          <StyledSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'running', label: 'Running' },
              { value: 'stopped', label: 'Stopped' },
              { value: 'starting', label: 'Starting' },
              { value: 'error', label: 'Error' },
            ]}
          />
          {!isTemplateTab && (
            <StyledSelect
              value={accessFilter}
              onChange={setAccessFilter}
              options={[
                { value: 'all', label: 'All Access' },
                { value: 'personal', label: 'Personal' },
                { value: 'specific', label: 'Specific Users' },
                { value: 'platform', label: 'Platform' },
              ]}
            />
          )}
          {!isTemplateTab && (
            <StyledSelect
              value={permissionFilter}
              onChange={setPermissionFilter}
              options={[
                { value: 'all', label: 'All Permissions' },
                { value: 'edit', label: 'Editor' },
                { value: 'view', label: 'View Only' },
              ]}
            />
          )}
          <StyledSelect
            value={sortBy}
            onChange={setSortBy}
            options={[
              { value: 'recent', label: 'Recently Updated' },
              { value: 'name-asc', label: 'Name A→Z' },
              { value: 'name-desc', label: 'Name Z→A' },
              { value: 'visitors', label: 'Most Visitors' },
            ]}
          />
          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground'
                }`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            {!isTemplateTab && (
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`p-2 rounded-lg border transition-colors ${
                  showFavoritesOnly
                    ? 'border-yellow-500/50 bg-yellow-500/10 text-yellow-500'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-border'
                }`}
                title={showFavoritesOnly ? 'Show all apps' : 'Show favorites only'}
              >
                <Star className="w-4 h-4" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs — left-aligned with underline */}
      {hasAnyApps && (
        <div className="flex items-center gap-1 mb-4 border-b border-border overflow-x-auto">
          {([
            { value: 'my-apps' as const, label: 'My Apps', count: apps.length },
            { value: 'shared' as const, label: 'Shared', count: sharedApps.length },
            { value: 'all' as const, label: 'All', count: mergedApps.length },
            ...(user.isAdmin && templateApps.length > 0
              ? [{ value: 'templates' as const, label: 'Templates', count: templateApps.length }]
              : []),
          ]).map(tab => (
            <button
              key={tab.value}
              onClick={() => setSourceFilter(tab.value)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap flex items-center gap-1.5 ${
                sourceFilter === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  sourceFilter === tab.value ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Folder section — acts as filter, stays visible (hidden on templates tab) */}
      {hasAnyApps && !isTemplateTab && (
        <FolderSection
          folders={folders}
          activeFolderId={activeFolderId}
          collapsed={foldersCollapsed}
          onToggleCollapse={() => setFoldersCollapsed(!foldersCollapsed)}
          onFolderClick={(id) => {
            // Toggle: click active folder to deselect, click another to select
            if (activeFolderId === id) {
              navigateToRoot()
            } else {
              navigateToFolder(id)
            }
          }}
          onClearFolder={() => navigateToRoot()}
          onCreateFolder={(name) => createFolder(name)}
          onRenameFolder={(id, name) => renameFolder(id, name)}
          onDeleteFolder={(id) => deleteFolderAction(id)}
          onChangeFolderColor={(id, color) => changeFolderColor(id, color)}
          onAddAppToFolder={(folderId, appName) => addAppToFolder(folderId, appName)}
          getFolderAppNames={getFolderAppNames}
          availableApps={mergedApps}
        />
      )}

      {hasActiveFilters && (
        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <button onClick={clearFilters} className="text-primary hover:underline">
            Clear filters
          </button>
        </div>
      )}

      {/* Unified app list */}
      {!hasAnyApps ? (
        deletedApps.length > 0 ? (
          <NoActiveAppsState onCreateClick={() => setShowCreate(true)} />
        ) : (
          <EmptyState onCreateClick={() => setShowCreate(true)} />
        )
      ) : filteredApps.length === 0 ? (
        <div className="text-center py-10 sm:py-16 rounded-xl border border-dashed border-border">
          {showFavoritesOnly ? (
            <>
              <Star className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No favorites yet</h3>
              <p className="text-muted-foreground mb-4">
                Click the heart icon on any app to add it to your favorites.
              </p>
              <button onClick={() => setShowFavoritesOnly(false)} className="text-primary hover:underline text-sm">Show all apps</button>
            </>
          ) : activeFolderId ? (
            <>
              <Search className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No apps in this folder</h3>
              <p className="text-muted-foreground mb-4">
                Right-click an app and use "Move to folder" to add apps here.
              </p>
              <button onClick={() => navigateToRoot()} className="text-primary hover:underline text-sm">Clear folder filter</button>
            </>
          ) : sourceFilter === 'templates' ? (
            <>
              <LayoutTemplate className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No templates match your filters</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filter criteria.
              </p>
              <button onClick={clearFilters} className="text-primary hover:underline text-sm">Clear all filters</button>
            </>
          ) : sourceFilter === 'shared' && sharedApps.length === 0 ? (
            <>
              <Users className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No shared apps yet</h3>
              <p className="text-muted-foreground">
                When someone shares an app with you, it will appear here.
              </p>
            </>
          ) : (
            <>
              <Search className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No apps match your filters</h3>
              <p className="text-muted-foreground mb-4">
                {deletedSearchMatches > 0
                  ? `${deletedSearchMatches} matching ${deletedSearchMatches === 1 ? 'app is' : 'apps are'} in Recently Deleted below.`
                  : 'Try adjusting your search or filter criteria.'}
              </p>
              <button onClick={clearFilters} className="text-primary hover:underline text-sm">Clear all filters</button>
            </>
          )}
        </div>
      ) : isTemplateTab ? (
        /* Template cards — use same card components as regular apps, but with restricted actions */
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredApps.map((app) => (
              <BrowseGridCard
                key={app.id}
                app={app}
                catCfg={app.category ? categoryConfig[app.category] : null}
                actions={
                  <AppActions
                    app={app}
                    permission="owner"
                    onStart={() => handleStart(app)}
                    onStop={() => handleStop(app)}
                    onRebuild={() => handleRebuild(app)}
                    onRename={() => navigate('/admin/templates')}
                    onDelete={() => handleDelete(app)}
                    size="sm"
                  />
                }
              />
            ))}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredApps.map((app) => (
              <BrowseListCard
                key={app.id}
                app={app}
                catCfg={app.category ? categoryConfig[app.category] : null}
                actions={
                  <AppActions
                    app={app}
                    permission="owner"
                    onStart={() => handleStart(app)}
                    onStop={() => handleStop(app)}
                    onRebuild={() => handleRebuild(app)}
                    onRename={() => navigate('/admin/templates')}
                    onDelete={() => handleDelete(app)}
                    size="lg"
                  />
                }
              />
            ))}
          </div>
        )
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredApps.map((app) => {
            const props = getAppProps(app)
            return (
              <AppContextMenu
                key={app.id}
                app={app}
                permission={props.permission}
                folders={folders}
                appFolderIds={getAppFolderIds(app.name)}
                onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                onStart={props.onStart}
                onStop={props.onStop}
                onRebuild={props.onRebuild}
                onRename={props.onRename}
                onPublish={props.onPublish}
                onShare={props.onShare}
                onClone={props.onClone}
                onDelete={props.onDelete}
              >
                {props.isOwned ? (
                  <GridAppCard
                    app={app}
                    folders={folders}
                    appFolderIds={getAppFolderIds(app.name)}
                    onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                    onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                    onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                    onStart={() => handleStart(app)}
                    onStop={() => handleStop(app)}
                    onRebuild={() => handleRebuild(app)}
                    onRename={() => setEditApp(app)}
                    onPublish={() => navigate(`/apps/${app.name}`)}
                    onShare={() => setShowShare(app)}
                    onClone={() => setCloneTarget(app)}
                    onDelete={() => handleDelete(app)}
                    onToggleFavorite={handleToggleFavorite}
                  />
                ) : (
                  <BrowseGridCard
                    app={app}
                    catCfg={app.category ? categoryConfig[app.category] : null}
                    onToggleFavorite={handleToggleFavorite}
                    bottomBadges={<PermissionBadge permission={app.my_permission} />}
                    actions={
                      <AppActions
                        app={app}
                        permission={props.permission}
                        folders={folders}
                        appFolderIds={getAppFolderIds(app.name)}
                        onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                        onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                        onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                        onStart={props.onStart}
                        onStop={props.onStop}
                        onRebuild={props.onRebuild}
                        onRename={props.onRename}
                        onPublish={props.onPublish}
                        onClone={props.onClone}
                        onShare={props.onShare}
                        onDelete={props.onDelete}
                        size="sm"
                      />
                    }
                  />
                )}
              </AppContextMenu>
            )
          })}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredApps.map((app) => {
            const props = getAppProps(app)
            return (
              <AppContextMenu
                key={app.id}
                app={app}
                permission={props.permission}
                folders={folders}
                appFolderIds={getAppFolderIds(app.name)}
                onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                onStart={props.onStart}
                onStop={props.onStop}
                onRebuild={props.onRebuild}
                onRename={props.onRename}
                onPublish={props.onPublish}
                onShare={props.onShare}
                onClone={props.onClone}
                onDelete={props.onDelete}
              >
                {props.isOwned ? (
                  <AppCard
                    app={app}
                    folders={folders}
                    appFolderIds={getAppFolderIds(app.name)}
                    onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                    onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                    onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                    onStart={() => handleStart(app)}
                    onStop={() => handleStop(app)}
                    onRebuild={() => handleRebuild(app)}
                    onRename={() => setEditApp(app)}
                    onPublish={() => navigate(`/apps/${app.name}`)}
                    onShare={() => setShowShare(app)}
                    onClone={() => setCloneTarget(app)}
                    onDelete={() => handleDelete(app)}
                    onToggleFavorite={handleToggleFavorite}
                  />
                ) : (
                  <BrowseListCard
                    app={app}
                    catCfg={app.category ? categoryConfig[app.category] : null}
                    onToggleFavorite={handleToggleFavorite}
                    bottomBadges={<PermissionBadge permission={app.my_permission} />}
                    actions={
                      <AppActions
                        app={app}
                        permission={props.permission}
                        folders={folders}
                        appFolderIds={getAppFolderIds(app.name)}
                        onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                        onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                        onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                        onStart={props.onStart}
                        onStop={props.onStop}
                        onRebuild={props.onRebuild}
                        onRename={props.onRename}
                        onPublish={props.onPublish}
                        onClone={props.onClone}
                        onShare={props.onShare}
                        onDelete={props.onDelete}
                        size="lg"
                      />
                    }
                  />
                )}
              </AppContextMenu>
            )
          })}
        </div>
      )}

      {/* Recently Deleted tray */}
      {showDeletedTray && (
        <RecentlyDeletedTray
          apps={filteredDeletedApps}
          totalCount={deletedApps.length}
          searchActive={search !== ''}
          restoringAppNames={restoringAppNames}
          permanentlyDeletingAppNames={permanentlyDeletingAppNames}
          onRestore={handleRestore}
          onPermanentDelete={handlePermanentDelete}
          open={showDeletedApps}
          onToggle={() => setShowDeletedApps(open => !open)}
        />
      )}

      {workspaceLaunchMessage && (
        <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="rounded-xl border border-border bg-card px-6 py-5 shadow-lg flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <div className="text-sm">
              <p className="font-medium">Opening workspace...</p>
              <p className="text-muted-foreground">{workspaceLaunchMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="text-center py-10 sm:py-16 rounded-xl border border-dashed border-border">
      <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
        <Code className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No apps yet</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Create your first app to get started. Your app will be instantly
        available at a unique URL and ready to develop.
      </p>
      <button
        onClick={onCreateClick}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Create Your First App
      </button>
    </div>
  )
}

function NoActiveAppsState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="text-center py-10 sm:py-16 rounded-xl border border-dashed border-border">
      <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
        <Code className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-2">No active apps</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        All your apps have been deleted. You can restore them from Recently Deleted below, or create a new app.
      </p>
      <button
        onClick={onCreateClick}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Create New App
      </button>
    </div>
  )
}

function RecentlyDeletedTray({
  apps,
  totalCount,
  searchActive,
  restoringAppNames,
  permanentlyDeletingAppNames,
  onRestore,
  onPermanentDelete,
  open,
  onToggle,
}: {
  apps: DeletedApp[]
  totalCount: number
  searchActive: boolean
  restoringAppNames: string[]
  permanentlyDeletingAppNames: string[]
  onRestore: (app: DeletedApp) => void
  onPermanentDelete: (app: DeletedApp) => void
  open: boolean
  onToggle: () => void
}) {
  const displayCount = searchActive ? `${apps.length} of ${totalCount}` : `${totalCount}`
  return (
    <div className="mt-8 border-t border-border pt-4">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Trash2 className="w-4 h-4" />
        Recently Deleted
        <span className="bg-secondary px-1.5 py-0.5 rounded text-xs">{displayCount}</span>
      </button>
      {open && (
        apps.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            No deleted apps match your search.
          </div>
        ) : (
          <div className="grid gap-2 mt-2">
            {apps.map((app) => {
              const isRestoring = restoringAppNames.includes(app.name)
              const isPermanentlyDeleting = permanentlyDeletingAppNames.includes(app.name)
              const deadline = new Date(app.restore_deadline)
              const daysLeft = Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
              return (
                <div
                  key={app.name}
                  className={`flex items-center justify-between p-3 rounded-lg border border-border bg-card/50 ${(isRestoring || isPermanentlyDeleting) ? 'opacity-50' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{app.display_name || app.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock3 className="w-3 h-3" />
                        {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left to restore
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onRestore(app)}
                      disabled={isRestoring || isPermanentlyDeleting}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:bg-secondary transition-colors text-sm disabled:opacity-50"
                    >
                      {isRestoring ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                      )}
                      Restore
                    </button>
                    <ConfirmDialog
                      title="Delete app permanently"
                      description={`Permanently delete "${app.display_name || app.name}"? This cannot be undone.`}
                      confirmLabel="Delete permanently"
                      loadingLabel="Deleting permanently..."
                      variant="destructive"
                      onConfirm={() => onPermanentDelete(app)}
                      trigger={
                        <button
                          disabled={isRestoring || isPermanentlyDeleting}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors text-sm disabled:opacity-50"
                        >
                          {isPermanentlyDeleting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                          Delete permanently
                        </button>
                      }
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )
      )}
    </div>
  )
}

/* ---------- Original app card components ---------- */

function GridAppCard({
  app,
  folders,
  appFolderIds,
  onMoveToFolder,
  onRemoveFromFolder,
  onCreateFolderAndMove,
  onStart,
  onStop,
  onRebuild,
  onRename,
  onPublish,
  onShare,
  onClone,
  onDelete,
  onToggleFavorite,
}: {
  app: App
  folders?: Folder[]
  appFolderIds?: number[]
  onMoveToFolder?: (folderId: number) => void
  onRemoveFromFolder?: (folderId: number) => void
  onCreateFolderAndMove?: (name: string) => void
  onStart: () => Promise<void>
  onStop: () => Promise<void>
  onRebuild: () => Promise<void>
  onRename?: () => void
  onPublish?: () => void
  onShare: () => void
  onClone?: () => void
  onDelete: () => Promise<void>
  onToggleFavorite: (app: App) => void
}) {
  const navigate = useNavigate()
  const AccessIcon = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.icon || Lock
  const accessLabel = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.label || 'Personal'
  const catCfg = app.category ? categoryConfig[app.category] : null

  return (
    <div
      className="p-3.5 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors cursor-pointer flex flex-col"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        if (app.status === 'error' || !app.url) {
          navigate(`/apps/${app.name}`)
        } else {
          window.open(app.url, '_blank', 'noopener,noreferrer')
        }
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <StatusBadge status={app.status} />
        </div>
        <div className="flex items-center gap-1">
          <FavoriteButton app={app} onToggle={onToggleFavorite} />
          <AppActions
            app={app}
            onStart={onStart}
            onStop={onStop}
            onRebuild={onRebuild}
            onRename={onRename}
            onPublish={onPublish}
            onShare={onShare}
            onClone={onClone}
            onDelete={onDelete}
            folders={folders}
            appFolderIds={appFolderIds}
            onMoveToFolder={onMoveToFolder}
            onRemoveFromFolder={onRemoveFromFolder}
            onCreateFolderAndMove={onCreateFolderAndMove}
            size="sm"
          />
        </div>
      </div>

      <h3 className="font-semibold truncate mb-1 flex items-center gap-1" title={app.display_name || app.name}>
        <span className="truncate">{app.display_name || app.name}</span>
        {app.is_featured && <FeaturedIcon />}
        {!app.is_featured && (app.unique_visitors_30d || 0) >= 3 && <PopularIcon />}
      </h3>

      <p className="text-xs text-muted-foreground line-clamp-2 mb-3 flex-1 min-h-[2lh]" title={app.description || undefined}>
        {app.description || 'No description'}
      </p>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="flex items-center gap-2.5 text-xs text-muted-foreground min-w-0 overflow-hidden">
          <span className="flex items-center gap-1">
            <AccessIcon className="w-3 h-3" />
            <span className="hidden sm:inline">{accessLabel}</span>
          </span>
          <span className="hidden md:inline whitespace-nowrap">Updated {formatRelativeTime(app.updated_at)}</span>
          <VisitorBadge count={app.visit_count_30d} />
          {catCfg && <span className={`text-[11px] ${catCfg.text} truncate`}>#{catCfg.label}</span>}
        </div>
        <AvatarWithTooltip name={app.owner_name} email={app.owner_email} />
      </div>
    </div>
  )
}

function AppCard({
  app,
  folders,
  appFolderIds,
  onMoveToFolder,
  onRemoveFromFolder,
  onCreateFolderAndMove,
  onStart,
  onStop,
  onRebuild,
  onRename,
  onPublish,
  onShare,
  onClone,
  onDelete,
  onToggleFavorite,
}: {
  app: App
  folders?: Folder[]
  appFolderIds?: number[]
  onMoveToFolder?: (folderId: number) => void
  onRemoveFromFolder?: (folderId: number) => void
  onCreateFolderAndMove?: (name: string) => void
  onStart: () => Promise<void>
  onStop: () => Promise<void>
  onRebuild: () => Promise<void>
  onRename?: () => void
  onPublish?: () => void
  onShare: () => void
  onClone?: () => void
  onDelete: () => Promise<void>
  onToggleFavorite: (app: App) => void
}) {
  const navigate = useNavigate()
  const AccessIcon = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.icon || Lock
  const accessLabel = accessLevelConfig[app.access_level as keyof typeof accessLevelConfig]?.label || 'Personal'
  const statusInfo = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
  const catCfg = app.category ? categoryConfig[app.category] : null

  return (
    <div
      className="px-3.5 py-2.5 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors cursor-pointer"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        if (app.status === 'error' || !app.url) {
          navigate(`/apps/${app.name}`)
        } else {
          window.open(app.url, '_blank', 'noopener,noreferrer')
        }
      }}
    >
      <div className="flex items-center gap-3">
        <FavoriteButton app={app} onToggle={onToggleFavorite} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="relative w-2 h-2 shrink-0" title={statusInfo.label}>
              <span className={`absolute inset-0 rounded-full ${statusInfo.color}`} />
              {statusInfo.pulse && <span className={`absolute inset-0 rounded-full ${statusInfo.color} animate-ping`} />}
            </span>
            <h3 className="text-sm font-semibold truncate" title={app.display_name || app.name}>
              {app.display_name || app.name}
            </h3>
            {app.is_featured && <FeaturedIcon />}
            {!app.is_featured && (app.unique_visitors_30d || 0) >= 3 && <PopularIcon />}
            {catCfg && <span className={`text-[11px] ${catCfg.text}`}>#{catCfg.label}</span>}
          </div>
          {app.description && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5" title={app.description}>{app.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
            <AccessIcon className="w-3 h-3" />
            {accessLabel}
          </span>
          <span className="hidden md:inline text-xs text-muted-foreground">
            Updated {formatRelativeTime(app.updated_at)}
          </span>
          <VisitorBadge count={app.visit_count_30d} />
          <AppActions
            app={app}
            onStart={onStart}
            onStop={onStop}
            onRebuild={onRebuild}
            onRename={onRename}
            onPublish={onPublish}
            onShare={onShare}
            onClone={onClone}
            onDelete={onDelete}
            folders={folders}
            appFolderIds={appFolderIds}
            onMoveToFolder={onMoveToFolder}
            onRemoveFromFolder={onRemoveFromFolder}
            onCreateFolderAndMove={onCreateFolderAndMove}
            size="lg"
          />
        </div>
      </div>
    </div>
  )
}
